/**
 * In-cart freebies picker + image lightbox.
 * Dynamic title, carousel arrows, gray-out when gift limit reached.
 */
(function () {
  'use strict';

  var THRESHOLDS = [0];
  var root = null;
  var lightbox = null;
  var lastFocus = null;
  var bound = false;

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function getRoot() {
    root = document.getElementById('tb-cart-freebies') || qs('[data-tb-cart-freebies]');
    return root;
  }

  function getLightbox() {
    lightbox = document.getElementById('tb-freebie-lightbox');
    return lightbox;
  }

  function getAllowedGiftCount(rupees) {
    /* Always max 1 free gift per order */
    return rupees > 0 ? 1 : 0;
  }

  function getCart() {
    if (window.TBUtils && typeof window.TBUtils.getCart === 'function') {
      return window.TBUtils.getCart();
    }
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function addToCart(payload) {
    if (window.TBUtils && typeof window.TBUtils.addToCart === 'function') {
      return window.TBUtils.addToCart(payload);
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) {
          var err = new Error((data && data.description) || r.statusText);
          err.responseJSON = data;
          throw err;
        }
        return data;
      });
    });
  }

  function refreshCart() {
    if (window.TBMiniCart && typeof window.TBMiniCart.refresh === 'function') {
      return Promise.resolve(window.TBMiniCart.refresh());
    }
    if (typeof window.refreshCart === 'function') {
      window.refreshCart(true);
    }
    return Promise.resolve();
  }

  function openLightbox(src, alt) {
    var lb = getLightbox();
    if (!lb || !src) return;
    var img = qs('[data-tb-freebie-lightbox-img]', lb);
    if (img) {
      img.src = src;
      img.alt = alt || 'Freebie preview';
    }
    lastFocus = document.activeElement;
    if (lb.parentNode !== document.body) {
      document.body.appendChild(lb);
    }
    lb.hidden = false;
    lb.removeAttribute('hidden');
    lb.setAttribute('aria-hidden', 'false');
    lb.classList.remove('is-open');
    void lb.offsetWidth;
    requestAnimationFrame(function () {
      lb.classList.add('is-open');
    });
    var closeBtn = qs('.tb-freebie-lightbox__close', lb);
    if (closeBtn && closeBtn.focus) closeBtn.focus();
  }

  function closeLightbox() {
    var lb = getLightbox();
    if (!lb) return;
    lb.classList.remove('is-open');
    setTimeout(function () {
      if (lb.classList.contains('is-open')) return;
      lb.hidden = true;
      lb.setAttribute('aria-hidden', 'true');
      var img = qs('[data-tb-freebie-lightbox-img]', lb);
      if (img) img.removeAttribute('src');
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try {
          lastFocus.focus();
        } catch (e) {}
      }
    }, 200);
  }

  function buildTitle(paidRupees, allowed, freeUnits) {
    var remaining = Math.max(0, allowed - freeUnits);

    if (allowed <= 0) {
      return 'Add a product to unlock your free gift';
    }

    if (remaining <= 0) {
      return "You've picked your free gift (1 per order)";
    }

    return 'Pick 1 free gift — included with every order';
  }

  function updateTitle(paidRupees, allowed, freeUnits) {
    var el = getRoot();
    if (!el) return;
    var title = qs('[data-tb-freebies-title]', el);
    if (!title) return;
    title.textContent = buildTitle(paidRupees, allowed, freeUnits);
  }

  function updateArrowState(el) {
    var track = qs('[data-tb-freebies-track]', el);
    var prev = qs('[data-tb-freebies-prev]', el);
    var next = qs('[data-tb-freebies-next]', el);
    if (!track || !prev || !next) return;

    /* Guard against duplicate arrow nodes from stale refresh HTML */
    qsa('[data-tb-freebies-prev]', el).forEach(function (btn, i) {
      if (i > 0) btn.remove();
    });
    qsa('[data-tb-freebies-next]', el).forEach(function (btn, i) {
      if (i > 0) btn.remove();
    });
    prev = qs('[data-tb-freebies-prev]', el);
    next = qs('[data-tb-freebies-next]', el);
    if (!prev || !next) return;

    var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    var left = track.scrollLeft;
    var atStart = left <= 4;
    var atEnd = left >= maxScroll - 4;
    var showArrows = maxScroll > 8;

    if (!showArrows) {
      prev.hidden = true;
      next.hidden = true;
      prev.disabled = true;
      next.disabled = true;
      return;
    }

    prev.hidden = atStart;
    next.hidden = atEnd;
    prev.disabled = atStart;
    next.disabled = atEnd;
  }

  function scrollTrack(dir) {
    var el = getRoot();
    if (!el) return;
    var track = qs('[data-tb-freebies-track]', el);
    if (!track) return;
    var amount = Math.max(140, Math.floor(track.clientWidth * 0.75));
    track.scrollBy({ left: dir * amount, behavior: 'smooth' });
    setTimeout(function () {
      updateArrowState(el);
    }, 280);
  }

  function syncEligibility(cart) {
    var el = getRoot();
    if (!el) return;

    var guard = window.TBFreeGiftGuard;
    var paidCents = 0;
    var freeUnits = 0;

    if (guard && typeof guard.getPaidSubtotalCents === 'function') {
      paidCents = guard.getPaidSubtotalCents(cart);
    } else {
      paidCents = Number(cart.items_subtotal_price || cart.total_price || 0);
    }

    if (guard && typeof guard.countFreeGiftUnits === 'function') {
      freeUnits = guard.countFreeGiftUnits(cart);
    } else {
      freeUnits = (cart.items || []).reduce(function (sum, item) {
        if (/mini|freebie|free-gift|free-sample/i.test(String(item.handle || ''))) {
          return sum + Number(item.quantity || 0);
        }
        return sum;
      }, 0);
    }

    var paidRupees = paidCents / 100;
    var allowed = getAllowedGiftCount(paidRupees);
    var locked = allowed <= 0 || freeUnits >= allowed;

    el.classList.toggle('is-locked', locked);
    el.classList.toggle('is-unlocked', !locked);
    el.setAttribute('data-allowed', String(allowed));
    el.setAttribute('data-free-units', String(freeUnits));

    updateTitle(paidRupees, allowed, freeUnits);

    var inCartHandles = {};
    (cart.items || []).forEach(function (item) {
      if (!item) return;
      var isGift =
        guard && typeof guard.isFreeGiftLine === 'function'
          ? guard.isFreeGiftLine(item)
          : /mini|freebie|free-gift|free-sample/i.test(String(item.handle || ''));
      if (isGift && item.handle) inCartHandles[String(item.handle).toLowerCase()] = true;
    });

    qsa('[data-tb-freebie]', el).forEach(function (card) {
      var handle = (card.getAttribute('data-product-handle') || '').toLowerCase();
      var inCart = !!inCartHandles[handle];
      var isAdding = card.classList.contains('is-adding');
      var disabled = locked && !inCart && !isAdding;

      card.classList.toggle('is-in-cart', inCart);
      card.classList.toggle('is-disabled', disabled && !isAdding);

      var btn = qs('[data-tb-freebie-add]', card);
      if (!btn) return;
      if (isAdding) {
        btn.disabled = true;
        return;
      }
      if (inCart) {
        btn.textContent = 'ADDED';
        btn.disabled = true;
        btn.classList.add('is-added');
        btn.classList.remove('is-grayed');
      } else {
        btn.textContent = 'ADD TO CART';
        btn.disabled = disabled || locked;
        btn.classList.remove('is-added');
        btn.classList.toggle('is-grayed', disabled || locked);
      }
    });

    updateArrowState(el);
  }

  function refreshState() {
    return getCart()
      .then(function (cart) {
        window.__tbLastCartForGifts = cart;
        syncEligibility(cart);
        return cart;
      })
      .catch(function () {});
  }

  function currentVariantId(card) {
    var shade = qs('[data-tb-freebie-shade]', card);
    if (shade && shade.value) return shade.value;
    var btn = qs('[data-tb-freebie-add]', card);
    return (btn && btn.getAttribute('data-variant-id')) || card.getAttribute('data-variant-id');
  }

  function onAdd(btn) {
    var card = btn.closest('[data-tb-freebie]');
    if (!card || btn.disabled || card.classList.contains('is-disabled')) return;

    var handle = btn.getAttribute('data-product-handle') || card.getAttribute('data-product-handle');
    var variantId = currentVariantId(card);
    if (!variantId) {
      alert('Please select a shade');
      return;
    }

    btn.disabled = true;
    var original = btn.textContent;
    btn.textContent = '...';
    card.classList.add('is-adding');
    card.classList.remove('is-disabled');

    if (window.TBCartUpsell && typeof window.TBCartUpsell.suppress === 'function') {
      window.TBCartUpsell.suppress(1500);
    }

    getCart()
      .then(function (cart) {
        var guard = window.TBFreeGiftGuard;
        if (guard && typeof guard.canAddFreeGift === 'function') {
          if (!guard.canAddFreeGift(cart, handle, variantId)) {
            if (typeof guard.showBlockedMessage === 'function') {
              guard.showBlockedMessage(cart);
            } else {
              alert('Free gift limit reached for your current order value.');
            }
            throw new Error('blocked');
          }
        }
        return addToCart({ id: Number(variantId), quantity: 1 });
      })
      .then(function () {
        btn.textContent = 'ADDED';
        card.classList.add('is-in-cart');
        card.classList.remove('is-disabled', 'is-adding');
        return refreshCart();
      })
      .then(function () {
        return refreshState();
      })
      .catch(function (err) {
        card.classList.remove('is-adding');
        if (!err || err.message !== 'blocked') {
          console.error('[tb-freebies]', err);
          var msg =
            (err && err.responseJSON && err.responseJSON.description) ||
            (err && err.message) ||
            'Could not add freebie';
          if (msg !== 'blocked') alert(msg);
        }
        btn.textContent = original;
        btn.disabled = false;
        refreshState();
      });
  }

  function onClick(e) {
    if (e.target.closest('[data-tb-freebies-prev]')) {
      e.preventDefault();
      scrollTrack(-1);
      return;
    }
    if (e.target.closest('[data-tb-freebies-next]')) {
      e.preventDefault();
      scrollTrack(1);
      return;
    }

    var zoom = e.target.closest('[data-tb-freebie-zoom]');
    if (zoom) {
      e.preventDefault();
      openLightbox(zoom.getAttribute('data-zoom-src'), zoom.getAttribute('data-zoom-alt'));
      return;
    }

    if (e.target.closest('[data-tb-freebie-lightbox-close]')) {
      e.preventDefault();
      closeLightbox();
      return;
    }

    var addBtn = e.target.closest('[data-tb-freebie-add]');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      onAdd(addBtn);
      return;
    }

    var shadeBtn = e.target.closest('[data-tb-freebie-shade-btn]');
    if (shadeBtn) {
      e.preventDefault();
      e.stopPropagation();
      var card = shadeBtn.closest('[data-tb-freebie]');
      if (!card) return;
      var vid = shadeBtn.getAttribute('data-variant-id');
      qsa('[data-tb-freebie-shade-btn]', card).forEach(function (btn) {
        var on = btn === shadeBtn;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      card.setAttribute('data-variant-id', vid);
      var add = qs('[data-tb-freebie-add]', card);
      if (add) add.setAttribute('data-variant-id', vid);
      var select = qs('[data-tb-freebie-shade]', card);
      if (select) select.value = vid;
      var zoomSrc = shadeBtn.getAttribute('data-zoom-src');
      var media = qs('[data-tb-freebie-zoom]', card);
      if (media && zoomSrc) media.setAttribute('data-zoom-src', zoomSrc);
      var img = qs('.tb-cart-freebie__img', card);
      if (img && zoomSrc) img.src = zoomSrc.replace(/width=\d+/, 'width=360');
    }
  }

  function onShadeChange(e) {
    var shade = e.target.closest('[data-tb-freebie-shade]');
    if (!shade) return;
    var card = shade.closest('[data-tb-freebie]');
    if (!card) return;
    var btn = qs('[data-tb-freebie-add]', card);
    if (btn) btn.setAttribute('data-variant-id', shade.value);
    card.setAttribute('data-variant-id', shade.value);

    var opt = shade.options[shade.selectedIndex];
    var zoomSrc = opt && opt.getAttribute('data-zoom-src');
    var media = qs('[data-tb-freebie-zoom]', card);
    if (media && zoomSrc) media.setAttribute('data-zoom-src', zoomSrc);
  }

  function scrollToFreebies(e) {
    var link =
      e.target &&
      e.target.closest &&
      e.target.closest('[data-tb-mc-gifts-scroll], a[href="#tb-cart-freebies"]');
    if (!link) return;
    e.preventDefault();
    e.stopPropagation();
    var el = getRoot();
    if (!el) return;
    if (typeof window.tbOpenCartSidebar === 'function') window.tbOpenCartSidebar();
    setTimeout(function () {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 120);
  }

  function bindTrackScroll() {
    document.addEventListener(
      'scroll',
      function (e) {
        var track = e.target && e.target.closest && e.target.closest('[data-tb-freebies-track]');
        if (!track) return;
        var el = getRoot();
        if (el) updateArrowState(el);
      },
      true
    );
  }

  function bind() {
    if (bound) {
      refreshState();
      return;
    }
    bound = true;

    document.addEventListener('click', onClick, true);
    document.addEventListener('change', onShadeChange, true);
    document.addEventListener('click', scrollToFreebies, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeLightbox();
    });
    bindTrackScroll();

    document.addEventListener('tb:cart:refreshed', function () {
      refreshState();
    });
    document.addEventListener('cart:updated', function () {
      refreshState();
    });

    window.TBCartFreebies = {
      refresh: refreshState,
      openLightbox: openLightbox,
      closeLightbox: closeLightbox
    };

    refreshState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
